import { useMemo } from 'react';
import { CurrencyType } from '@tiendanube/common';
import { VirtualInteractiveList } from 'commons/components';
import { ProductListItem } from './ProductListItem';
import { ProductInterface, SelectedProductInterface } from '../types';

interface ProductListProps<Minimalistic extends boolean> {
  products: ProductInterface<false>[];
  selectedProducts: SelectedProductInterface[];
  minimalistic?: boolean;
  selectedVariantsLimitReached?: boolean;
  currencyDisplay?: CurrencyType;
  showPrice: boolean;
  allowInfiniteStockSelection: boolean;
  onChange: (
    selectedProducts: ProductInterface<Minimalistic>[],
    checked: boolean,
  ) => void;
  onReached: () => void;
  isRefreshing?: boolean;
}

interface ProductListItemContainerProps<Minimalistic extends boolean> {
  selectedProducts: SelectedProductInterface[];
  index: number;
  productIds: string[];
  products: ProductInterface<false>[];
  minimalistic?: boolean;
  selectedVariantsLimitReached?: boolean;
  currencyDisplay?: CurrencyType;
  showPrice: boolean;
  allowInfiniteStockSelection: boolean;
  onChange: (
    selectedProducts: ProductInterface<Minimalistic>[],
    checked: boolean,
  ) => void;
}

function ProductListItemContainer<Minimalistic extends boolean>({
  index,
  productIds,
  products,
  selectedProducts,
  onChange,
  minimalistic,
  currencyDisplay,
  selectedVariantsLimitReached,
  showPrice,
  allowInfiniteStockSelection,
}: Readonly<ProductListItemContainerProps<Minimalistic>>) {
  const variants = useMemo(
    () => products.filter((p) => p.productId === productIds[index]),
    [products, productIds, index],
  );
  const selectedVariants = useMemo(
    () =>
      selectedProducts.filter(
        (sProduct) => sProduct.productId === productIds[index],
      ),
    [selectedProducts, productIds, index],
  );

  return (
    <ProductListItem
      variants={variants}
      selectedVariants={selectedVariants}
      minimalistic={minimalistic}
      onChange={onChange}
      selectedVariantsLimitReached={selectedVariantsLimitReached}
      currencyDisplay={currencyDisplay}
      showPrice={showPrice}
      allowInfiniteStockSelection={allowInfiniteStockSelection}
    />
  );
}

function ProductList<Minimalistic extends boolean>({
  onChange,
  onReached,
  products,
  selectedProducts,
  minimalistic,
  selectedVariantsLimitReached,
  currencyDisplay,
  isRefreshing = false,
  showPrice,
  allowInfiniteStockSelection,
}: Readonly<ProductListProps<Minimalistic>>) {
  const productIds = useMemo(
    () => [...new Set(products.map((product) => product.productId))],
    [products],
  );

  return (
    <VirtualInteractiveList
      totalCount={productIds.length}
      endReached={onReached}
      showFooter={isRefreshing}
      itemContent={(index) => (
        <ProductListItemContainer
          index={index}
          productIds={productIds}
          products={products}
          selectedProducts={selectedProducts}
          onChange={onChange}
          minimalistic={minimalistic}
          selectedVariantsLimitReached={selectedVariantsLimitReached}
          currencyDisplay={currencyDisplay}
          showPrice={showPrice}
          allowInfiniteStockSelection={allowInfiniteStockSelection}
        />
      )}
    />
  );
}

export default ProductList;
